import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useOktaAuth } from '@okta/okta-react';
import { IFeedbackRequest } from '../models/iProductSearchRequest';
import { submitSearchFeedback } from '../providers/productFinderAPI';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import Popover from '@mui/material/Popover';
import { useState } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { toast } from 'react-toastify';

export interface ISearchFeedbackProps {
  searchId: string | null;
}

type FeedbackType = 'THUMBS_UP' | 'THUMBS_DOWN';

const SearchFeedback = ({ searchId }: ISearchFeedbackProps) => {
  const { authState } = useOktaAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [comment, setComment] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackType>();

  /**
   * Sets the anchor element to the event target (thmb down icon) to open the comment box
   * @param event
   */
  const handleNegativeFeedbackClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Clears the comment and anchor element
   */
  const handleCommentBoxClose = () => {
    setAnchorEl(null);
    setComment('');
  };

  /**
   * Submit feedback and close the comment box
   * @param type
   */
  const handleFeedback = async (rating: FeedbackType, comment?: string) => {
    // Don't allow feedback if there's no searchId
    if (!searchId) {
      toast.info('Feedback can only be submitted for a completed search.');
      return;
    }

    if (authState?.accessToken?.accessToken) {
      const feedbackPayload: IFeedbackRequest = {
        correlationId: searchId,
        feedbackFlag: rating === 'THUMBS_UP',
        feedbackText: comment,
      };

      try {
        await submitSearchFeedback(authState.accessToken.accessToken, feedbackPayload);
        setSelectedFeedback(rating); // Show visual confirmation
        toast.success('Thank you for your feedback!');

        // Close the comment box and clear the comment
        if (rating === 'THUMBS_DOWN') {
          handleCommentBoxClose();
        }
      } catch (error) {
        toast.error('Failed to submit feedback.');
      }
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', pr: 2 }}>
      <IconButton
        onClick={() => handleFeedback('THUMBS_UP')}
        title={'Good response'}
        sx={{ color: selectedFeedback === 'THUMBS_UP' ? '#03387E' : '' }}
      >
        <ThumbUpIcon />
      </IconButton>
      <IconButton
        onClick={handleNegativeFeedbackClick}
        title={'Bad response'}
        sx={{ color: selectedFeedback === 'THUMBS_DOWN' ? '#03387E' : '' }}
      >
        <ThumbDownIcon />
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleCommentBoxClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <div style={{ padding: '10px' }}>
          <TextField
            label="Comment"
            multiline
            rows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
          />
          <Button variant="contained" onClick={() => handleFeedback('THUMBS_DOWN', comment)} sx={{ mt: 1 }}>
            Submit
          </Button>
        </div>
      </Popover>
    </Box>
  );
};

export default SearchFeedback;
